import { useState, useEffect, useCallback } from 'react';
import { api, AdminPaymentsDashboard, AdminPaymentsCosts } from '@/lib/api';

type Period = '7d' | '30d' | '90d';

export function PaymentsDashboard() {
  const [period, setPeriod] = useState<Period>('30d');
  const [dashboard, setDashboard] = useState<AdminPaymentsDashboard | null>(null);
  const [costs, setCosts] = useState<AdminPaymentsCosts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refund state
  const [refundPaymentId, setRefundPaymentId] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundResult, setRefundResult] = useState<string | null>(null);

  // Grant state
  const [grantUserId, setGrantUserId] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantDescription, setGrantDescription] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantResult, setGrantResult] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashData, costData] = await Promise.all([
        api.adminGetPaymentsDashboard(period),
        api.adminGetPaymentsCosts(period),
      ]);
      setDashboard(dashData);
      setCosts(costData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefund = async () => {
    if (!refundPaymentId.trim()) return;
    setRefundLoading(true);
    setRefundResult(null);
    try {
      await api.adminProcessRefund(refundPaymentId.trim(), refundReason.trim() || undefined);
      setRefundResult('Refund processed successfully');
      setRefundPaymentId('');
      setRefundReason('');
      fetchData();
    } catch (err) {
      setRefundResult(`Error: ${err instanceof Error ? err.message : 'Failed'}`);
    } finally {
      setRefundLoading(false);
    }
  };

  const handleGrant = async () => {
    if (!grantUserId.trim() || !grantAmount) return;
    setGrantLoading(true);
    setGrantResult(null);
    try {
      const result = await api.adminGrantCredits(
        grantUserId.trim(),
        parseFloat(grantAmount),
        grantDescription.trim() || undefined,
      );
      setGrantResult(`Granted. New balance: $${(result.newBalance / 100).toFixed(2)}`);
      setGrantUserId('');
      setGrantAmount('');
      setGrantDescription('');
    } catch (err) {
      setGrantResult(`Error: ${err instanceof Error ? err.message : 'Failed'}`);
    } finally {
      setGrantLoading(false);
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-foreground mb-8">Payments & Revenue</h1>
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-muted rounded-lg" />
            ))}
          </div>
          <div className="h-64 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-foreground">Payments & Revenue</h1>
        <div className="flex items-center gap-2">
          {(['7d', '30d', '90d'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-sm rounded-md ${
                period === p
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-3 py-1.5 text-sm bg-muted text-muted-foreground hover:bg-accent rounded-md disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {dashboard && (
        <>
          {/* ── Global Profit & Loss ── */}
          <div className="bg-card rounded-lg border border-border p-6 mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Global Profit & Loss</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Revenue */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {dashboard.profitAndLoss.revenue.display}
                </p>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  <p>
                    Purchases: {dashboard.profitAndLoss.revenue.purchases.display} (
                    {dashboard.profitAndLoss.revenue.purchases.count})
                  </p>
                  <p>
                    Subscriptions: {dashboard.profitAndLoss.revenue.subscriptions.display} (
                    {dashboard.profitAndLoss.revenue.subscriptions.activeCount} active)
                  </p>
                </div>
              </div>

              {/* Expense */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Provider Cost</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  {dashboard.profitAndLoss.expense.display}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboard.profitAndLoss.expense.transactionCount} billable operations
                </p>
              </div>

              {/* System Credits Absorbed */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">System Credits Given</p>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                  {dashboard.profitAndLoss.systemCreditsAbsorbed.display}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboard.profitAndLoss.systemCreditsAbsorbed.grantCount} grants (cost absorbed
                  by Fias)
                </p>
              </div>

              {/* Gross Margin */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Gross Margin</p>
                <p
                  className={`text-3xl font-bold ${
                    dashboard.profitAndLoss.grossMargin.isPositive
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {dashboard.profitAndLoss.grossMargin.display}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboard.profitAndLoss.grossMargin.percent}% margin
                </p>
              </div>
            </div>
          </div>

          {/* ── System Credit Economics (Marketing Expense) ── */}
          <div className="bg-card rounded-lg border border-border p-6 mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-1">System Credit Economics</h2>
            <p className="text-xs text-muted-foreground mb-4">
              System credits granted to users and the resulting AI provider costs (marketing
              expense)
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-sm text-muted-foreground">Total Granted</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {dashboard.profitAndLoss.systemCreditsAbsorbed.display}
                </p>
                <p className="text-xs text-muted-foreground">
                  {dashboard.profitAndLoss.systemCreditsAbsorbed.grantCount} grants
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recipients</p>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.profitAndLoss.systemCreditUsage.recipients}
                </p>
                <p className="text-xs text-muted-foreground">users received system credits</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Usage by Recipients</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {dashboard.profitAndLoss.systemCreditUsage.recipientUsage.display}
                </p>
                <p className="text-xs text-muted-foreground">
                  {dashboard.profitAndLoss.systemCreditUsage.recipientUsage.transactionCount}{' '}
                  operations
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Cost / Recipient</p>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.profitAndLoss.systemCreditUsage.recipients > 0
                    ? `$${(dashboard.profitAndLoss.systemCreditUsage.recipientUsage.totalConsumed / dashboard.profitAndLoss.systemCreditUsage.recipients / 100).toFixed(2)}`
                    : '$0.00'}
                </p>
                <p className="text-xs text-muted-foreground">per recipient this period</p>
              </div>
            </div>

            {/* Grant type breakdown */}
            {dashboard.profitAndLoss.systemCreditUsage.byGrantType.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Grants by Type</h3>
                <div className="space-y-2">
                  {dashboard.profitAndLoss.systemCreditUsage.byGrantType.map((gt) => (
                    <div key={gt.grantType} className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-foreground">
                          {gt.grantType === 'welcome' ? 'Welcome Grants' : 'Monthly Replenishments'}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">({gt.count})</span>
                      </div>
                      <span className="text-sm font-mono text-foreground">{gt.display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Usage category breakdown */}
            {dashboard.profitAndLoss.systemCreditUsage.byCategory.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  What system credit recipients use
                </h3>
                <div className="space-y-2">
                  {dashboard.profitAndLoss.systemCreditUsage.byCategory.map((cat) => (
                    <div key={cat.category} className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-foreground">{cat.category}</span>
                        <span className="text-xs text-muted-foreground ml-2">({cat.count})</span>
                      </div>
                      <span className="text-sm font-mono text-foreground">{cat.display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Platform Stats ── */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-card rounded-lg border border-border p-4">
              <p className="text-sm text-muted-foreground">Total Users</p>
              <p className="text-2xl font-bold text-foreground">{dashboard.platform.totalUsers}</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-4">
              <p className="text-sm text-muted-foreground">Premium Subscribers</p>
              <p className="text-2xl font-bold text-foreground">
                {dashboard.platform.activeSubscribers}
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-4">
              <p className="text-sm text-muted-foreground">Avg Revenue / User</p>
              <p className="text-2xl font-bold text-foreground">
                {dashboard.platform.averageRevenuePerUser}
              </p>
            </div>
          </div>

          {/* ── Expense Breakdown by Category ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Expense by Category</h2>
              {dashboard.expenseByCategory.length === 0 ? (
                <p className="text-sm text-muted-foreground">No expense data for this period</p>
              ) : (
                <div className="space-y-3">
                  {dashboard.expenseByCategory.map((cat) => (
                    <div key={cat.category} className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-foreground">{cat.category}</span>
                        <span className="text-xs text-muted-foreground ml-2">({cat.count})</span>
                      </div>
                      <span className="text-sm font-mono text-foreground">{cat.display}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {costs && (
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Top Arches by Cost</h2>
                {costs.byArche.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No arche cost data</p>
                ) : (
                  <div className="space-y-3">
                    {costs.byArche.slice(0, 10).map((arche) => (
                      <div key={arche.archeId} className="flex items-center justify-between">
                        <div className="truncate mr-4">
                          <span className="text-sm font-medium text-foreground">
                            {arche.archeName}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            ({arche.count})
                          </span>
                        </div>
                        <span className="text-sm font-mono text-foreground whitespace-nowrap">
                          {arche.displayCost}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Top Spending Users ── */}
          <div className="bg-card rounded-lg border border-border p-6 mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Top Spending Users</h2>
            {dashboard.topSpendingUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No spending data for this period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">User ID</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">
                        Total Spent
                      </th>
                      <th className="text-left py-2 text-muted-foreground font-medium">
                        Transactions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.topSpendingUsers.map((user) => (
                      <tr key={user.userId} className="border-b border-border/50">
                        <td className="py-2 text-foreground font-mono text-xs">{user.userId}</td>
                        <td className="py-2 text-foreground font-medium">{user.displaySpent}</td>
                        <td className="py-2 text-muted-foreground">{user.transactionCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Admin Actions ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Refund */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Process Refund</h2>
              <div className="space-y-3">
                <input
                  type="text"
                  value={refundPaymentId}
                  onChange={(e) => setRefundPaymentId(e.target.value)}
                  placeholder="Payment ID (pay_...)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <input
                  type="text"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <button
                  onClick={handleRefund}
                  disabled={refundLoading || !refundPaymentId.trim()}
                  className="w-full px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive rounded-lg hover:bg-destructive/90 disabled:opacity-50"
                >
                  {refundLoading ? 'Processing...' : 'Process Refund'}
                </button>
                {refundResult && (
                  <p
                    className={`text-sm ${refundResult.startsWith('Error') ? 'text-destructive' : 'text-green-600'}`}
                  >
                    {refundResult}
                  </p>
                )}
              </div>
            </div>

            {/* Grant Credits */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Grant Credits</h2>
              <div className="space-y-3">
                <input
                  type="text"
                  value={grantUserId}
                  onChange={(e) => setGrantUserId(e.target.value)}
                  placeholder="User ID"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <input
                  type="number"
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  placeholder="Amount (credits)"
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <input
                  type="text"
                  value={grantDescription}
                  onChange={(e) => setGrantDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <button
                  onClick={handleGrant}
                  disabled={grantLoading || !grantUserId.trim() || !grantAmount}
                  className="w-full px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {grantLoading ? 'Granting...' : 'Grant Credits'}
                </button>
                {grantResult && (
                  <p
                    className={`text-sm ${grantResult.startsWith('Error') ? 'text-destructive' : 'text-green-600'}`}
                  >
                    {grantResult}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
